/**
 * The jobs an operator can be doing when they capture something.
 *
 * `mode` says how the pixels were taken (photo, document, burst); this says
 * what they are for, and it travels with the capture to the extraction
 * model, where `apps/capture-api/src/extraction/capture-workflows.ts` turns
 * the same id into the instruction the model reads beside the image. The two
 * files mirror each other deliberately: neither app imports the other, which
 * is the same arrangement the wire types already use (see `api/types.ts`).
 *
 * Adding one means adding it here, in the API's `CaptureWorkflow` union, in
 * its instruction map, and in INTERFACES.md section 1.
 *
 * The titles and taglines are the client's own words, ported from the Base44
 * proof of concept's `WORKFLOWS`.
 */

import type { CaptureWorkflow } from './api/types';

export interface CaptureWorkflowOption {
  id: CaptureWorkflow;
  title: string;
  /** One line, in the operator's terms, naming what they walk away with. */
  tagline: string;
}

export const CAPTURE_WORKFLOWS: readonly CaptureWorkflowOption[] = [
  {
    id: 'mro_spare_parts',
    title: 'Spare part',
    tagline: 'Name a worn or unlabelled part.',
  },
  {
    id: 'mdm_quality',
    title: 'Fill a record',
    tagline: 'Complete a product record that is missing attributes.',
  },
  {
    id: 'procurement_request',
    title: 'New purchase',
    tagline: 'Identify an item nobody here has bought before.',
  },
  {
    id: 'quality_inspection',
    title: 'Defect or return',
    tagline: 'Log damage with the item it belongs to.',
  },
  {
    id: 'compliance_audit',
    title: 'Asset check',
    tagline: 'Verify an asset against the registry.',
  },
  {
    id: 'inventory_recon',
    title: 'Stock count',
    tagline: 'Count what is on the shelf.',
  },
];

const STORAGE_KEY = 'smart-capture:workflow';

/**
 * The operator works a run of the same job, so the choice is remembered
 * rather than asked once per item. It survives a reload and a reopened app,
 * which is what a stock take looks like in practice.
 *
 * An unknown stored value is discarded rather than sent: the API refuses a
 * workflow it does not know, and a build that dropped one would otherwise
 * fail every capture until the operator noticed.
 */
export function readStoredWorkflow(): CaptureWorkflow | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return CAPTURE_WORKFLOWS.some((option) => option.id === stored)
      ? (stored as CaptureWorkflow)
      : null;
  } catch {
    // Private browsing and blocked site data both throw here.
    return null;
  }
}

export function storeWorkflow(workflow: CaptureWorkflow | null): void {
  try {
    if (workflow) localStorage.setItem(STORAGE_KEY, workflow);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // The choice still applies for this session; it just will not be
    // remembered the next time the app opens.
  }
}

export function workflowTitle(workflow: CaptureWorkflow): string {
  return CAPTURE_WORKFLOWS.find((option) => option.id === workflow)?.title ?? workflow;
}
